import React from 'react';

import {Button, Icon, Text} from '@albert-io/atomic';
import {history} from 'client/history';
import {NoSearchResults} from 'client/TeacherDashboard/components/NoSearchResults';

import type {StatusSegment} from './constants';
import {CREATE_ASSIGNMENT_PATH, STATUS_SEGMENTS} from './constants';

// A ghost assignment row: icon badge + two skeleton bars, mirroring a real row.
const SkeletonCard = () => (
  <div className='flex w-[232px] items-center gap-x1-half rounded-x1-half border border-subtle background-primary p-x1'>
    <div className='flex h-x4-half w-x4-half shrink-0 items-center justify-center rounded-full background-brand-subtle-hover'>
      <Icon className='text-s' color='brand' icon='file-lines' iconStyle='regular' />
    </div>
    <div className='flex grow flex-col gap-x0-half'>
      <div className='h-x1 w-[100px] rounded-full background-bold' />
      <div className='h-x1 w-x5 rounded-full background-bold' />
    </div>
  </div>
);

// Two overlapping ghost rows — the decorative illustration above the empty-state copy.
const EmptyIllustration = () => (
  <div aria-hidden='true' className='grid place-items-start'>
    <div className='col-start-1 row-start-1'>
      <SkeletonCard />
    </div>
    <div className='col-start-1 row-start-1 mt-x4 ml-x4 rounded-x1-half elevation-shadow-l2'>
      <SkeletonCard />
    </div>
  </div>
);

const EmptyState = ({
  illustration,
  action,
  children
}: {
  illustration: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <div className='flex w-full flex-col items-center gap-x3 py-x8 text-center'>
    {illustration}
    {children}
    {action}
  </div>
);

const CreateAssignmentButton = () => (
  <Button onClick={() => history.pushState(null, CREATE_ASSIGNMENT_PATH)} roundness='pill'>
    Create New Assignment
  </Button>
);

/** Whole scope (all classes or one class) has no assignments — renders alone, below the header. */
export const AssignmentsOnboardingEmptyState = () => (
  <EmptyState action={<CreateAssignmentButton />} illustration={<EmptyIllustration />}>
    <div className='flex flex-col gap-x1'>
      <Text as='div' bold size='l'>
        No Assignments Yet!
      </Text>
      <Text as='div' className='max-w-[440px]' color='secondary'>
        Create, view, organize, and manage assignments all in one place.
      </Text>
    </div>
  </EmptyState>
);

/** Scope has assignments, but the active status tab has none — renders under the controls. */
export const AssignmentStatusEmptyState = ({segment}: {segment: StatusSegment}) => {
  const label = STATUS_SEGMENTS.find((s) => s.value === segment)?.label ?? '';
  return (
    <EmptyState action={<CreateAssignmentButton />} illustration={<EmptyIllustration />}>
      <Text as='div' size='l'>
        No {label} Assignments
      </Text>
    </EmptyState>
  );
};

/** The active filters/search matched nothing. No reset action — the user adjusts filters/search. */
export const AssignmentsFilteredEmptyState = () => <NoSearchResults />;
